import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Send, Loader2, CheckCircle, AlertTriangle, Lightbulb, Bug } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";

// Pilot configuration
const PILOT_CONFIG = {
  agencyName: "A Touch of Angels",
  version: "v1.0-pilot",
  startDate: "2026-07-02",
};

// Feedback categories
const FEEDBACK_CATEGORIES = [
  { id: "friction", label: "Workflow Friction", icon: AlertTriangle, color: "text-amber-500" },
  { id: "enhancement", label: "Enhancement Idea", icon: Lightbulb, color: "text-blue-500" },
  { id: "bug", label: "Bug Report", icon: Bug, color: "text-red-500" },
  { id: "praise", label: "Works Great!", icon: CheckCircle, color: "text-emerald-500" },
];

export default function PilotBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [showFeedback, setShowFeedback] = useState(false);
  const [category, setCategory] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim() || !category) return;

    setSubmitting(true);
    try {
      await api.post("/pilot/feedback", {
        category,
        feedback: feedbackText.trim(),
        page: location.pathname,
        page_title: document.title,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });

      toast.success("Feedback submitted! Thank you for helping improve Leynix.");
      setFeedbackText("");
      setCategory("");
      setShowFeedback(false);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Pilot Banner */}
      <div className="bg-gradient-to-r from-[#06372B] to-[#0a4a3a] text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          <span className="font-semibold">{PILOT_CONFIG.agencyName} Pilot</span>
          <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
            {PILOT_CONFIG.version}
          </span>
        </div>
        
        <button
          onClick={() => setShowFeedback(true)}
          data-testid="pilot-feedback-button"
          className="flex items-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
        >
          <MessageSquarePlus className="w-4 h-4" />
          <span className="hidden sm:inline">Submit Feedback</span>
        </button>
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowFeedback(false)}
          />
          
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#06372B] to-[#0a4a3a] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg">Pilot Feedback</h2>
                <p className="text-white/70 text-sm">Help us improve Leynix</p>
              </div>
              <button
                onClick={() => setShowFeedback(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Context info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="grid grid-cols-2 gap-2 text-gray-600">
                  <div>
                    <span className="text-gray-400">Page:</span>{" "}
                    <span className="font-medium">{location.pathname}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">User:</span>{" "}
                    <span className="font-medium">{user?.name || user?.email}</span>
                  </div>
                </div>
              </div>

              {/* Category selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What type of feedback is this?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {FEEDBACK_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      data-testid={`feedback-category-${cat.id}`}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                        category === cat.id
                          ? "border-[#06372B] bg-[#06372B]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <cat.icon className={`w-5 h-5 ${cat.color}`} />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Feedback text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us more
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={
                    category === "friction"
                      ? "What workflow felt difficult or confusing?"
                      : category === "enhancement"
                      ? "What feature or improvement would help?"
                      : category === "bug"
                      ? "What happened? What did you expect to happen?"
                      : category === "praise"
                      ? "What worked well for you?"
                      : "Share your feedback..."
                  }
                  rows={4}
                  data-testid="feedback-text"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#06372B]/20 focus:border-[#06372B] transition-all resize-none"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFeedback(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!category || !feedbackText.trim() || submitting}
                  data-testid="submit-feedback-button"
                  className="bg-[#06372B] hover:bg-[#0a4a3a]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
